import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Container, Form, Button } from "react-bootstrap";
import {
  BsDownload,
  BsArrowClockwise,
  BsExclamationCircle,
  BsShieldLock,
  BsFileEarmarkArrowDown,
  BsXCircle,
} from "react-icons/bs";
import Filedownload from "js-file-download";
import ApiService from "../ApiService";
import { SnackbarContext } from "./AlertSystem";
import {
  validateTypeformUrl,
  messageForDownloadStatus,
  resolveDownloadFilename,
  previewKindForContentType,
} from "../util/typeformAttachment";
import "./WispAttachmentDownloadPage.css";

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const WispAttachmentDownloadPage = () => {
  const [url, setUrl] = useState("");
  const [validationError, setValidationError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const { showSnackbar } = useContext(SnackbarContext) || {};
  const inputRef = useRef(null);
  // Track the active object URL so we can revoke it (avoid leaks) when it is replaced
  // or when the component unmounts.
  const objectUrlRef = useRef(null);

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      revokeObjectUrl();
    };
  }, [revokeObjectUrl]);

  const handleUrlChange = (event) => {
    setUrl(event.target.value);
    setValidationError(null);
    setDownloadError(null);
  };

  const handleDownload = useCallback(async () => {
    const { valid, error } = validateTypeformUrl(url);
    if (!valid) {
      setValidationError(error);
      inputRef.current?.focus();
      return;
    }

    const requestUrl = url.trim();
    setValidationError(null);
    setDownloadError(null);
    setLoading(true);
    try {
      const response = await ApiService.downloadWispTypeformAttachment(requestUrl);
      const blob = response?.data;
      if (!(blob instanceof Blob)) {
        throw new Error("Unexpected response from server");
      }

      const contentType =
        response?.headers?.["content-type"] || blob.type || "application/octet-stream";
      const filename = resolveDownloadFilename(
        response?.headers?.["content-disposition"],
        requestUrl
      );

      // Replace any previous preview URL before creating a new one.
      revokeObjectUrl();
      const previewKind = previewKindForContentType(contentType);
      const objectUrl = URL.createObjectURL(blob);
      objectUrlRef.current = objectUrl;

      setResult({
        blob,
        filename,
        contentType,
        size: blob.size,
        previewKind,
        objectUrl,
      });
      showSnackbar?.({
        message: `Attachment ready: ${filename}`,
        severity: "success",
        autoHide: 6000,
      });
    } catch (err) {
      const status = err?.response?.status;
      const message = messageForDownloadStatus(status);
      setResult(null);
      revokeObjectUrl();
      setDownloadError(message);
      showSnackbar?.({ message, severity: "error", autoHide: 8000 });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const handleKeyDown = (event) => {
    if (event.key !== "Enter") return;
    if ((event.target?.tagName || "").toLowerCase() !== "input") return;
    event.preventDefault();
    if (loading) return;
    handleDownload();
  };

  const handleSaveFile = () => {
    if (!result?.blob) return;
    Filedownload(result.blob, result.filename);
  };

  const handleClear = () => {
    setUrl("");
    setValidationError(null);
    setDownloadError(null);
    setResult(null);
    revokeObjectUrl();
    inputRef.current?.focus();
  };

  const hasUrl = url.trim().length > 0;
  const sizeLabel = result ? formatFileSize(result.size) : null;

  return (
    <Container fluid className="wad-container">
      <div className="wad-header">
        <h1 className="wad-title">Typeform Attachment Download</h1>
        <p className="wad-subtitle">
          Paste a locked Typeform file link (for example an insurance card uploaded through
          Wisp&nbsp;PrEP). The portal downloads it securely through the TBD backend and hands you
          the file &mdash; the Typeform link is never opened directly in your browser.
        </p>
      </div>

      <div className="wad-card">
        <Form onKeyDown={handleKeyDown} className="wad-form">
          <div className="wad-field">
            <Form.Label className="wad-label" htmlFor="wad-url">
              Typeform file URL
            </Form.Label>
            <Form.Control
              id="wad-url"
              ref={inputRef}
              type="text"
              inputMode="url"
              placeholder="https://api.typeform.com/responses/files/…"
              value={url}
              onChange={handleUrlChange}
              className="wad-input"
              autoComplete="off"
              aria-invalid={validationError ? "true" : "false"}
              aria-describedby={validationError ? "wad-validation" : undefined}
            />
          </div>

          <div className="wad-actions">
            <Button
              type="button"
              className="wad-download-btn"
              onClick={handleDownload}
              disabled={loading || !hasUrl}
              aria-label="Download attachment"
            >
              {loading ? (
                <BsArrowClockwise size={16} className="wad-spin" />
              ) : (
                <BsDownload size={16} />
              )}
              {loading ? "Downloading…" : "Download"}
            </Button>
            {(hasUrl || result || downloadError) && (
              <button type="button" className="wad-clear-btn" onClick={handleClear}>
                <BsXCircle size={14} />
                Clear
              </button>
            )}
          </div>

          {validationError && (
            <div className="wad-validation" id="wad-validation" role="alert">
              <BsExclamationCircle size={16} className="wad-validation__icon" />
              <span>{validationError}</span>
            </div>
          )}
        </Form>

        <p className="wad-security-note">
          <BsShieldLock size={15} className="wad-security-note__icon" />
          Only <strong>https</strong> links on <strong>typeform.com</strong> are accepted. Files
          are fetched server-side with the Wisp access token — access is limited to authorized
          clinical staff.
        </p>
      </div>

      <div className="wad-result-section" aria-live="polite">
        {downloadError ? (
          <div className="wad-state wad-state--error" role="alert">
            <BsExclamationCircle size={40} className="wad-state__icon wad-state__icon--error" />
            <p className="wad-state__title">Download failed</p>
            <p className="wad-state__text">{downloadError}</p>
            <Button
              type="button"
              className="wad-download-btn wad-retry-btn"
              onClick={handleDownload}
              disabled={loading || !hasUrl}
            >
              <BsArrowClockwise size={16} className={loading ? "wad-spin" : undefined} />
              Try again
            </Button>
          </div>
        ) : result ? (
          <div className="wad-file-card">
            <div className="wad-file-head">
              <BsFileEarmarkArrowDown size={26} className="wad-file-head__icon" />
              <div className="wad-file-head__meta">
                <span className="wad-file-head__name" title={result.filename}>
                  {result.filename}
                </span>
                <span className="wad-file-head__sub">
                  {result.contentType}
                  {sizeLabel ? ` · ${sizeLabel}` : ""}
                </span>
              </div>
              <Button type="button" className="wad-save-btn" onClick={handleSaveFile}>
                <BsDownload size={16} />
                Save file
              </Button>
            </div>

            {result.previewKind === "image" && (
              <div className="wad-preview">
                <img className="wad-preview__img" src={result.objectUrl} alt={result.filename} />
              </div>
            )}
            {result.previewKind === "pdf" && (
              <div className="wad-preview">
                <iframe
                  className="wad-preview__pdf"
                  src={result.objectUrl}
                  title={`Preview of ${result.filename}`}
                />
              </div>
            )}
            {!result.previewKind && (
              <p className="wad-preview__none">
                Preview isn&apos;t available for this file type. Use <strong>Save file</strong> to
                download it.
              </p>
            )}
          </div>
        ) : (
          <div className="wad-state">
            <BsFileEarmarkArrowDown size={40} className="wad-state__icon" />
            <p className="wad-state__title">No attachment yet</p>
            <p className="wad-state__text">
              Paste a Typeform file URL above and select <strong>Download</strong> to retrieve and
              preview the attachment.
            </p>
          </div>
        )}
      </div>
    </Container>
  );
};

export default WispAttachmentDownloadPage;
